import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, Users, Euro, Target, ArrowRight, Clock, CheckCircle, UserPlus, GraduationCap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TunnelVentePage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/stats/admin`, { credentials: 'include' });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading || !stats) return (
    <Layout>
      <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" /></div>
    </Layout>
  );

  const stages = [
    { name: 'Entreprises partenaires', count: stats.total_entreprises, color: 'bg-slate-500', percent: 100 },
    { name: 'Apprenants inscrits', count: stats.total_enrollments, color: 'bg-teal-400', percent: Math.round((stats.total_enrollments / Math.max(stats.total_entreprises, 1)) * 100) },
    { name: 'Formations suivies', count: stats.total_courses, color: 'bg-teal-500', percent: Math.round((stats.total_courses / Math.max(stats.total_entreprises, 1)) * 100) },
    { name: 'Évaluations terminées', count: stats.completed_evaluations, color: 'bg-emerald-500', percent: Math.round((stats.completed_evaluations / Math.max(stats.total_enrollments, 1)) * 100) },
  ];

  return (
    <Layout>
      <div className="space-y-6" data-testid="tunnel-vente-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tunnel de vente</h1>
          <p className="text-slate-500">Suivez le parcours de vos prospects jusqu'à l'inscription</p>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <Card><CardContent className="pt-6 text-center"><TrendingUp className="h-8 w-8 text-teal-500 mx-auto mb-2" /><p className="text-2xl font-bold" data-testid="stat-entreprises">{stats.total_entreprises}</p><p className="text-sm text-slate-500">Entreprises</p></CardContent></Card>
          <Card><CardContent className="pt-6 text-center"><Target className="h-8 w-8 text-teal-500 mx-auto mb-2" /><p className="text-2xl font-bold" data-testid="stat-enrollments">{stats.total_enrollments}</p><p className="text-sm text-slate-500">Inscriptions</p></CardContent></Card>
          <Card><CardContent className="pt-6 text-center"><Euro className="h-8 w-8 text-teal-500 mx-auto mb-2" /><p className="text-2xl font-bold" data-testid="stat-funded">{stats.total_funded?.toLocaleString('fr-FR')}&euro;</p><p className="text-sm text-slate-500">Financements</p></CardContent></Card>
          <Card><CardContent className="pt-6 text-center"><Users className="h-8 w-8 text-emerald-500 mx-auto mb-2" /><p className="text-2xl font-bold" data-testid="stat-students">{stats.total_students}</p><p className="text-sm text-slate-500">Apprenants</p></CardContent></Card>
        </div>

        {/* Funnel */}
        <Card>
          <CardHeader><CardTitle className="text-lg">Entonnoir de conversion</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stages.map((stage) => (
                <div key={stage.name} className="flex items-center gap-4">
                  <div className="w-48 text-sm font-medium text-slate-700">{stage.name}</div>
                  <div className="flex-1">
                    <div className="relative h-10 bg-slate-100 rounded-lg overflow-hidden">
                      <div className={`absolute inset-y-0 left-0 ${stage.color} rounded-lg transition-all flex items-center justify-end pr-3`} style={{ width: `${Math.max(stage.percent, 10)}%` }}>
                        <span className="text-white font-bold text-sm">{stage.count}</span>
                      </div>
                    </div>
                  </div>
                  <div className="w-16 text-right text-sm text-slate-500">{stage.percent}%</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Résumé */}
        <div className="grid grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle className="text-lg">Résumé des formations</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { icon: GraduationCap, label: 'Formations publiées', value: stats.published_courses, color: 'text-teal-500' },
                  { icon: UserPlus, label: 'Inscriptions totales', value: stats.total_enrollments, color: 'text-blue-500' },
                  { icon: CheckCircle, label: 'Taux de complétion éval.', value: `${stats.completion_rate}%`, color: 'text-emerald-500' },
                  { icon: Clock, label: 'Sessions à venir', value: stats.upcoming_sessions, color: 'text-teal-600' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <div className="flex items-center gap-3"><item.icon className={`h-5 w-5 ${item.color}`} /><span className="text-sm text-slate-700">{item.label}</span></div>
                    <span className="font-bold text-slate-900">{item.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-lg">Financements</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-teal-50 border border-teal-100">
                  <p className="text-sm text-teal-700">Total des financements</p>
                  <p className="text-3xl font-bold text-teal-700">{stats.total_funded?.toLocaleString('fr-FR')} &euro;</p>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <span className="text-sm text-slate-700">Nombre de financeurs</span>
                  <span className="font-bold text-slate-900">{stats.total_financeurs}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <span className="text-sm text-slate-700">Entreprises actives</span>
                  <span className="font-bold text-slate-900">{stats.total_entreprises}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default TunnelVentePage;
